// ════════════════════════════════════════════════════════════════
// 📜 B213 (AI_99) — trilha de auditoria das coleções clínicas
// ════════════════════════════════════════════════════════════════
// FONTE ÚNICA da escrita na coleção `trilha-auditoria` (espelha o
// padrão "fonte única" do B88/permissoesCategoria). Cada EDIÇÃO ou
// EXCLUSÃO de registro clínico grava aqui uma entrada permanente —
// a exclusão deixa sua "lápide" e o rastro sobrevive ao documento.
//
// As rules tornam a coleção append-only de verdade: criar é permitido
// a quem escreve nas coleções clínicas; editar/apagar entradas é
// negado a todos, inclusive admin.
//
// DECISÃO DE DESENHO (registrada na governança §6.3): a gravação é
// TOLERANTE A FALHA — se a escrita da trilha falhar, o fluxo clínico
// NÃO é interrompido (um exame não deixa de ser salvo porque o log
// falhou); a falha é registrada no console. Trade-off aceito para um
// item de qualidade de auditoria (o núcleo CFM já é coberto por
// B81/B110/B142).
//
// A entrada carrega um `rotulo` curto de identificação (ex.: o tipo do
// exame e a data) — NÃO o conteúdo clínico do registro.

import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from "../../lib/firebase";

export type ColecaoClinica =
  | "evolucoes"
  | "exames"
  | "exames-imagem"
  | "marcadores"
  | "sorologias"
  | "pendencias"
  | "prescricoes"
  | "documentos-enfermagem";

// Rótulos humanos por coleção (também usados pelo Log de Alterações).
export const ROTULO_COLECAO: Record<ColecaoClinica, string> = {
  evolucoes: "Evolução/Consulta",
  exames: "Exame laboratorial",
  "exames-imagem": "Exame de imagem",
  marcadores: "Marcadores/Autoanticorpos",
  sorologias: "Sorologias",
  pendencias: "Pendência",
  prescricoes: "Prescrição/Documento",
  "documentos-enfermagem": "Documento de Enfermagem",
};

interface UsuarioTrilha {
  id: string;
  nome: string;
}

interface BaseTrilha {
  colecao: ColecaoClinica;
  docId: string;
  pacienteId: string;
  /** Identificação curta do registro (ex.: "TC abdome — 12/05/2026"). Sem conteúdo clínico. */
  rotulo: string;
  usuario: UsuarioTrilha;
}

async function gravar(entrada: Record<string, any>): Promise<void> {
  try {
    await addDoc(collection(db, "trilha-auditoria"), {
      ...entrada,
      data: new Date().toISOString(),
      criadoEm: serverTimestamp(),
    });
  } catch (erro) {
    // Tolerante a falha por desenho (ver cabeçalho). Sem conteúdo
    // clínico no log (B196).
    console.warn("⚠️ Falha ao gravar trilha de auditoria:", erro);
  }
}

/** Registra uma EDIÇÃO de registro clínico. No-op se nada mudou. */
export async function registrarEdicaoTrilha(
  params: BaseTrilha & { camposAlterados: string[] }
): Promise<void> {
  if (params.camposAlterados.length === 0) return;
  await gravar({
    colecao: params.colecao,
    docId: params.docId,
    pacienteId: params.pacienteId,
    acao: "edicao",
    camposAlterados: params.camposAlterados,
    rotulo: params.rotulo,
    usuarioId: params.usuario.id,
    usuarioNome: params.usuario.nome,
  });
}

/** Registra uma EXCLUSÃO — a "lápide" que sobrevive ao documento. */
export async function registrarExclusaoTrilha(
  params: BaseTrilha
): Promise<void> {
  await gravar({
    colecao: params.colecao,
    docId: params.docId,
    pacienteId: params.pacienteId,
    acao: "exclusao",
    rotulo: params.rotulo,
    usuarioId: params.usuario.id,
    usuarioNome: params.usuario.nome,
  });
}
